import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { UserCheck, UserX, Shield } from 'lucide-react';
import clsx from 'clsx';
import { getUsers, activateUser, deactivateUser, getUserDriverDetails } from '../api/users';
import { useToast } from '../context/ToastContext';
import { Table } from '../components/ui/Table';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import SearchInput from '../components/ui/SearchInput';
import useDebouncedValue from '../hooks/useDebouncedValue';
import Pagination from '../components/ui/Pagination';
import Drawer from '../components/ui/Drawer';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import SummarizeButton from '../components/chat/SummarizeButton';
import { formatDate, formatCurrency, getErrorMessage } from '../lib/utils';

function DetailSection({ title, rows }) {
  return (
    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">{title}</h3>
      <dl className="space-y-3">
        {rows.map(([label, val]) => (
          <div key={label} className="flex justify-between gap-4">
            <dt className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</dt>
            <dd className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right break-all">{val ?? '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const DRIVER_STATUS_LABEL = { online: 'Online', offline: 'Offline', on_trip: 'On Trip' };

function DriverDetails({ userId }) {
  const { data: d, isLoading, error } = useQuery({
    queryKey: ['user-driver-details', userId],
    queryFn: () => getUserDriverDetails(userId).then((r) => r.data),
    retry: false,
  });

  if (isLoading) {
    return <p className="pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">Loading driver details…</p>;
  }
  if (error) {
    return (
      <p className="pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
        {error.response?.status === 404
          ? 'This driver has not completed registration yet.'
          : getErrorMessage(error, 'Failed to load driver details')}
      </p>
    );
  }

  const docs = d.documents ?? {};
  const sub = d.subscription;
  const subExpired = sub && new Date(`${sub.expires_at}Z`) < new Date();

  return (
    <>
      <DetailSection
        title="Driver"
        rows={[
          ['Driver ID', `#${d.driver_id}`],
          ['Approval', <Badge key="a" variant={d.is_approved ? 'success' : 'warning'}>{d.is_approved ? 'Approved' : 'Pending'}</Badge>],
          ['Current Status', DRIVER_STATUS_LABEL[d.status] ?? d.status],
          ['Gold Driver', d.is_gold ? 'Yes' : 'No'],
          ['Rating', d.rating != null ? `${d.rating} ★ (${d.rating_count})` : 'No ratings yet'],
          ['Completed Rides', d.completed_rides],
          ['Cancelled by Driver', d.cancelled_by_driver],
          ['Daily Goal', d.daily_goal != null ? formatCurrency(d.daily_goal) : null],
          ['Registered', formatDate(d.registered_at)],
          ['Last Location Update', d.last_location_at ? formatDate(d.last_location_at) : null],
        ]}
      />

      <DetailSection
        title="Wallets"
        rows={[
          ['Access Balance', d.access_wallet ? formatCurrency(d.access_wallet.balance) : null],
          ['Total Recharged', d.access_wallet ? formatCurrency(d.access_wallet.total_recharged) : null],
          ['Total Deducted', d.access_wallet ? formatCurrency(d.access_wallet.total_deducted) : null],
          ['Earnings Balance', d.earnings_wallet ? formatCurrency(d.earnings_wallet.balance) : null],
          ['Total Earned', d.earnings_wallet ? formatCurrency(d.earnings_wallet.total_earned) : null],
        ]}
      />

      <DetailSection
        title="Subscription"
        rows={sub ? [
          ['Plan', sub.plan_name],
          ['Status', <Badge key="s" variant={subExpired ? 'default' : 'success'}>{subExpired ? 'Expired' : 'Active'}</Badge>],
          ['Price Paid', formatCurrency(sub.price_paid)],
          ['Started', formatDate(sub.started_at)],
          ['Expires', formatDate(sub.expires_at)],
          ['Rides on Plan', sub.rides_completed],
        ] : [['Plan', 'No subscription']]}
      />

      <DetailSection
        title="Documents"
        rows={[
          ['Approved', docs.approved ?? 0],
          ['Pending Review', docs.pending ?? 0],
          ['Rejected', docs.rejected ?? 0],
        ]}
      />

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Vehicles</h3>
        {d.vehicles.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No vehicles added</p>
        ) : (
          <div className="space-y-2">
            {d.vehicles.map((v) => (
              <div key={v.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">{v.vehicle_number}</span>
                  <Badge variant={v.is_active ? 'success' : 'default'}>{v.is_active ? 'Active' : 'Inactive'}</Badge>
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {[v.vehicle_type, v.vehicle_model, v.vehicle_color, v.fuel_type].filter(Boolean).join(' · ')}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function UserDetail({ user }) {
  if (!user) return null;
  const fields = [
    ['ID', user.id],
    ['Full Name', user.full_name ?? user.name],
    ['Phone', user.phone],
    ['Email', user.email],
    ['Role', user.role],
    ['Status', user.is_active ? 'Active' : 'Inactive'],
    ['Verified', user.is_verified ? 'Yes' : 'No'],
    ['Joined', formatDate(user.created_at)],
  ];
  return (
    <div className="px-6 pt-4 pb-8 space-y-4">
      <div className="flex items-center justify-between gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 text-xl font-bold">
            {(user.full_name ?? user.name ?? 'U')[0].toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100">{user.full_name ?? user.name ?? '—'}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{user.email ?? user.phone}</p>
          </div>
        </div>
        <SummarizeButton
          recordText={fields.map(([label, val]) => `${label}: ${val ?? '—'}`).join('\n')}
        />
      </div>
      <dl className="space-y-3">
        {fields.map(([label, val]) => (
          <div key={label} className="flex justify-between gap-4">
            <dt className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</dt>
            <dd className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right break-all">{val ?? '—'}</dd>
          </div>
        ))}
      </dl>
      {user.role === 'driver' && <DriverDetails userId={user.id} />}
    </div>
  );
}

const PAGE_SIZE = 20;

const ROLE_TABS = [
  { key: 'customer', label: 'Customers' },
  { key: 'driver', label: 'Drivers' },
  { key: 'admin', label: 'Admins' },
];

export default function Users() {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [role, setRole] = useState('customer');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirm, setConfirm] = useState(null);

  function handleRoleChange(nextRole) {
    setRole(nextRole);
    setPage(1);
  }

  const debouncedSearch = useDebouncedValue(search.trim());

  // Searched on the server so it covers every user, not just the current page.
  const { data, isLoading } = useQuery({
    queryKey: ['users', role, page, debouncedSearch],
    queryFn: () =>
      getUsers({ page, limit: PAGE_SIZE, role, ...(debouncedSearch ? { search: debouncedSearch } : {}) }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const users = Array.isArray(data) ? data : (data?.items ?? data?.users ?? []);
  const total = Array.isArray(data) ? users.length : (data?.total ?? users.length);

  function handleSearchChange(value) {
    setSearch(value);
    setPage(1);
  }

  const activate = useMutation({
    mutationFn: (id) => activateUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      addToast('User activated successfully', 'success');
      setConfirm(null);
    },
    onError: () => addToast('Failed to activate user', 'error'),
  });

  const deactivate = useMutation({
    mutationFn: (id) => deactivateUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      addToast('User deactivated', 'success');
      setConfirm(null);
    },
    onError: () => addToast('Failed to deactivate user', 'error'),
  });

  const columns = [
    { key: 'id', label: 'ID', width: 60, render: (v) => <span className="font-mono text-xs text-gray-500">#{v}</span> },
    {
      key: 'full_name', label: 'Name',
      render: (v, row) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">{v ?? row.name ?? '—'}</span>
      ),
    },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email', render: (v) => <span className="text-gray-500 dark:text-gray-400">{v ?? '—'}</span> },
    {
      key: 'role', label: 'Role',
      render: (v) => <Badge variant="indigo">{v ?? 'user'}</Badge>,
    },
    {
      key: 'is_active', label: 'Status',
      render: (v) => <Badge variant={v ? 'success' : 'default'}>{v ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'is_verified', label: 'Verified',
      render: (v) => v ? (
        <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium">
          <Shield size={12} /> Verified
        </span>
      ) : <span className="text-gray-400 text-xs">—</span>,
    },
    { key: 'created_at', label: 'Joined', render: (v) => <span className="text-gray-500 text-xs whitespace-nowrap">{formatDate(v)}</span> },
    {
      key: 'actions', label: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {row.is_active ? (
            <Button
              size="xs"
              variant="danger"
              icon={UserX}
              onClick={() => setConfirm({ type: 'deactivate', user: row })}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              size="xs"
              variant="success"
              icon={UserCheck}
              onClick={() => setConfirm({ type: 'activate', user: row })}
            >
              Activate
            </Button>
          )}
        </div>
      ),
    },
  ];

  const isLoading2 = activate.isPending || deactivate.isPending;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Users</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} total</p>
        </div>
        <SearchInput
          placeholder="Search by name, phone, email or ID…"
          value={search}
          onChange={handleSearchChange}
        />
      </div>

      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit">
        {ROLE_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => handleRoleChange(t.key)}
            className={clsx(
              'px-4 py-1.5 rounded-lg text-sm font-medium transition-colors',
              role === t.key
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Table
        columns={columns}
        data={users}
        loading={isLoading}
        onRowClick={setSelectedUser}
        emptyMessage={debouncedSearch ? `No users match “${debouncedSearch}”` : 'No users found'}
      />

      <Pagination page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />

      <Drawer
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        title="User Details"
      >
        <UserDetail user={selectedUser} />
      </Drawer>

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.type === 'activate' ? 'Activate User' : 'Deactivate User'}
        message={
          confirm?.type === 'activate'
            ? `Activate ${confirm?.user?.full_name ?? confirm?.user?.name}? They will regain access to the platform.`
            : `Deactivate ${confirm?.user?.full_name ?? confirm?.user?.name}? They will lose access to the platform.`
        }
        confirmLabel={confirm?.type === 'activate' ? 'Activate' : 'Deactivate'}
        confirmVariant={confirm?.type === 'activate' ? 'success' : 'danger'}
        loading={isLoading2}
        onConfirm={() => {
          if (confirm?.type === 'activate') activate.mutate(confirm.user.id);
          else deactivate.mutate(confirm.user.id);
        }}
      />
    </div>
  );
}
